import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { X, MessageCircle } from "lucide-react";

const HIDDEN_PREFIXES = ["/admin", "/portal", "/login", "/register", "/book"];
const SESSION_KEY = "letstalk_dismissed";

export default function LetsTalkWidget() {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [shown, setShown] = useState(false); // controls entrance transition
  const [triggered, setTriggered] = useState(false); // popup has appeared at least once
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(SESSION_KEY) === "1");

  const hidden = HIDDEN_PREFIXES.some((p) => location.pathname.startsWith(p));

  useEffect(() => {
    if (hidden || dismissed) return;
    const t = setTimeout(() => { setOpen(true); setTriggered(true); }, 10000);
    return () => clearTimeout(t);
  }, [hidden, dismissed]);

  useEffect(() => {
    if (open) {
      const r = requestAnimationFrame(() => setShown(true));
      return () => cancelAnimationFrame(r);
    }
    setShown(false);
  }, [open]);

  const close = () => {
    setOpen(false);
    setDismissed(true);
    sessionStorage.setItem(SESSION_KEY, "1");
  };

  const reopen = () => { setOpen(true); setTriggered(true); };

  const goToForm = () => {
    setOpen(false);
    navigate("/contact");
    setTimeout(() => {
      const el = document.getElementById("contact-form");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 500);
  };

  if (hidden) return null;

  return (
    <>
      {open && (
        <div
          data-testid="lets-talk-popup"
          style={{ transition: "opacity .35s ease, transform .35s ease", opacity: shown ? 1 : 0, transform: shown ? "translateY(0)" : "translateY(16px)" }}
          className="fixed bottom-4 right-4 z-[60] w-[calc(100vw-2rem)] max-w-xs sm:w-80 rounded-2xl bg-white border border-black/10 shadow-xl shadow-black/10 p-5"
        >
          <button onClick={close} data-testid="lets-talk-close" aria-label="Close" className="absolute top-3 right-3 text-foreground/40 hover:text-foreground transition-colors">
            <X className="w-4 h-4" />
          </button>
          <div className="font-display font-extrabold text-lg text-[#0D1110]">Let's Talk! 👋</div>
          <p className="mt-1.5 text-sm text-foreground/70 leading-relaxed">
            Have questions about our El Salvador adventure? We're here to help.
          </p>
          <button
            onClick={goToForm}
            data-testid="lets-talk-cta"
            className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary text-white h-11 text-sm font-semibold tracking-wide uppercase transition-[transform,background-color,box-shadow] duration-200 hover:bg-[#B84726] hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary/20"
          >
            Contact Us
          </button>
        </div>
      )}

      {!open && (triggered || dismissed) && (
        <button
          onClick={reopen}
          data-testid="lets-talk-fab"
          className="fixed bottom-4 right-4 z-[60] inline-flex items-center gap-2 rounded-full bg-primary text-white pl-4 pr-5 h-12 text-sm font-semibold shadow-lg shadow-primary/25 transition-[transform,background-color] duration-200 hover:bg-[#B84726] hover:-translate-y-0.5"
        >
          <MessageCircle className="w-4 h-4" /> Let's Talk 💬
        </button>
      )}
    </>
  );
}
